import { getEnv } from '@luna/env';
import { createMutation, createQuery, request } from '@luna/api';
import { triggerGlobalEvent } from '@luna/events';
import { SettingsService } from '@api-client/dashboard';

import type { Settings, WebApiSettings } from './SettingsTypes';
import { maskTacPhoneNumbersIfExists } from './SettingsService';

type UpdateResponse = WebApiSettings & {
    settings?: WebApiSettings;
};

export const [
    useSettings,
    {
        queryApi: querySettings,
        queryApiIfNeeded: querySettingsIfNeeded,
        selectData: selectSettings,
        updateCache: updateSettingsCache,
    },
] = createQuery<() => Promise<Settings>>({
    name: 'user-settings/settings',
    payloadCreator: async () => {
        const dashboardSettings = await SettingsService.getPublicSettings();
        const webApiSettings = dashboardSettings._embedded.legacySettings as WebApiSettings;
        const webApiSettingsAfterMasking = maskTacPhoneNumbersIfExists(webApiSettings);

        // setTimeout assures the event is triggered only after store gets updated with pending: false
        setTimeout(() => {
            triggerGlobalEvent('settings:legacySyncEvent');
        }, 0);
        return {
            ...webApiSettingsAfterMasking,
            appearanceMode: dashboardSettings.appearanceMode ?? 'LIGHT',
        };
    },
});

export const [, { mutate: updateSettings }] = createMutation({
    name: 'user-settings/settings-update',
    payloadCreator: async (updatedSettings: Settings) => {
        const settingsUrl = `${getEnv().localApiBaseUrl}my/settings`;
        const response = await request.put<UpdateResponse>(settingsUrl, updatedSettings);

        triggerGlobalEvent('settings:legacySyncEvent');
        const responseAfterMasking = maskTacPhoneNumbersIfExists(response.settings ?? response);
        const settingsData = selectSettings();

        if (!settingsData) {
            return responseAfterMasking;
        }

        updateSettingsCache({
            data: { ...responseAfterMasking, appearanceMode: settingsData?.appearanceMode },
        });
        return responseAfterMasking;
    },
});
